import React, { useState } from 'react';

import { filterMatches, matchesOfPlayer } from './scoring-match-query';
import ScoringMatchCard from './scoring-match-card';
import ScoringPlayerMatchesModal from './scoring-player-matches-modal';
import ScoringSearchBox from './scoring-search-box';
import ScoringStandings from './scoring-standings';
import { runLabel, runsOfMatches } from './scoring-runs';
import { groupFormatLabel, usesPointSystem, type MatchItem, type ScoringGroup } from './scoring-types';

interface Props {
  group: ScoringGroup;
  matches: MatchItem[];
  token: string;
  canSubmit: boolean;
  showBack: boolean;
  onBack: () => void;
  onSaved: () => void;
}

export const ScoringGroupView: React.FC<Props> = ({
  group,
  matches,
  token,
  canSubmit,
  showBack,
  onBack,
  onSaved,
}) => {
  // A group opens fully collapsed — nothing is expanded until it is clicked, and
  // saving a result closes the card instead of jumping into the next match.
  const [openId, setOpenId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [pickedPlayerId, setPickedPlayerId] = useState<number | null>(null);

  const visibleMatches = filterMatches(matches, query);
  const runs = runsOfMatches(visibleMatches);
  const pickedPlayer =
    group.standings.find((row) => row.leaguePlayerId === pickedPlayerId) ?? null;

  function toggle(matchId: number): void {
    setOpenId(openId === matchId ? null : matchId);
  }

  function matchCard(match: MatchItem): JSX.Element {
    return (
      <ScoringMatchCard
        key={match.id}
        match={match}
        token={token}
        canSubmit={canSubmit}
        scoring={group.scoring}
        isOpen={openId === match.id}
        onToggle={() => toggle(match.id)}
        onSaved={handleSaved}
      />
    );
  }

  function handleSaved(): void {
    setOpenId(null);
    onSaved();
  }

  return (
    <section className="scoring-group">
      <header className="scoring-group__header">
        {showBack && (
          <button
            type="button"
            className="scoring-group__back"
            data-testid="scoring-back-to-groups"
            onClick={onBack}
          >
            ← Grupy
          </button>
        )}
        {group.name !== null && <h2 className="scoring-group__name">Grupa {group.name}</h2>}
        <span className="scoring-group__rules">{groupFormatLabel(group)}</span>
        <ScoringSearchBox value={query} onChange={setQuery} />
      </header>

      <ScoringStandings
        rows={group.standings}
        showPoints={usesPointSystem(group)}
        onPickPlayer={(row) => setPickedPlayerId(row.leaguePlayerId)}
      />

      <p className="scoring-hint">
        Kliknij zawodnika w tabeli — zobaczysz jego ranking i mecze w tej grupie.
      </p>

      {visibleMatches.length === 0 && query.trim() !== '' && (
        <p className="scoring-no-matches" data-testid="scoring-search-empty">
          Brak meczów dla „{query}".
        </p>
      )}

      <ul className="scoring-matches" data-testid="scoring-matches">
        {runs === null
          ? visibleMatches.map(matchCard)
          : runs.map((run) => (
              <React.Fragment key={run.number}>
                <li className="scoring-run-head" data-testid={`scoring-run-${run.number}`}>
                  {runLabel(run)}
                </li>
                {run.matches.map(matchCard)}
              </React.Fragment>
            ))}
      </ul>

      {pickedPlayer !== null && (
        <ScoringPlayerMatchesModal
          player={pickedPlayer}
          matches={matchesOfPlayer(matches, pickedPlayer.leaguePlayerId)}
          showPoints={usesPointSystem(group)}
          scoring={group.scoring}
          entry={{ token, canSubmit, onSaved: handleSaved }}
          onClose={() => setPickedPlayerId(null)}
        />
      )}
    </section>
  );
};

export default ScoringGroupView;
